export const buildCandidateList = (values, baseCandidates, currentValue) => {
  const cleaned = values.filter(value => Number.isFinite(value) && value > 0).sort((a, b) => a - b);
  if (!cleaned.length) return [];
  const max = cleaned[cleaned.length - 1];
  const unique = new Set();
  baseCandidates.forEach((value) => {
    if (value > 0 && value <= max) unique.add(Math.round(value));
  });
  if (Number.isFinite(currentValue) && currentValue > 0 && currentValue <= max) {
    unique.add(Math.round(currentValue));
  }
  const quantiles = [0.2, 0.4, 0.6, 0.8];
  quantiles.forEach((q) => {
    const idx = Math.floor((cleaned.length - 1) * q);
    if (idx >= 0 && idx < cleaned.length) unique.add(Math.round(cleaned[idx]));
  });
  unique.add(Math.round(max));
  return Array.from(unique).sort((a, b) => a - b);
};

export const recommendThresholds = (stats, stableMap, options = {}) => {
  if (!stats || stats.length === 0) return null;
  const {
    targetStableRate = 0.7,
    minEligibleRatio = 0.25,
    minEligibleCount = 6,
    current = {},
  } = options;
  const totalGroups = stats.length;
  const minEligible = Math.max(minEligibleCount, Math.ceil(totalGroups * minEligibleRatio));

  const eventCandidates = buildCandidateList(
    stats.map(item => item.eventCount),
    [10, 20, 30, 40, 50, 70, 100, 150],
    current.minEvents
  );
  const activeCandidates = buildCandidateList(
    stats.map(item => item.nonZeroYears),
    [5, 8, 10, 12, 15, 20],
    current.minNonZeroYears
  );
  const spanCandidates = buildCandidateList(
    stats.map(item => item.spanYears),
    [20, 30, 40, 50, 60, 70, 80],
    current.minSpanYears
  );

  if (!eventCandidates.length || !activeCandidates.length || !spanCandidates.length) return null;

  let best = null;
  eventCandidates.forEach((minEvents) => {
    activeCandidates.forEach((minNonZeroYears) => {
      spanCandidates.forEach((minSpanYears) => {
        const eligible = stats.filter(item => (
          item.eventCount >= minEvents
          && item.nonZeroYears >= minNonZeroYears
          && item.spanYears >= minSpanYears
        ));
        const eligibleCount = eligible.length;
        if (eligibleCount === 0) return;
        const stableCount = eligible.reduce((acc, item) => acc + (stableMap.get(item.key) ? 1 : 0), 0);
        const stableRate = stableCount / eligibleCount;
        const meetsTarget = stableRate >= targetStableRate && eligibleCount >= minEligible;
        const candidate = {
          minEvents,
          minNonZeroYears,
          minSpanYears,
          eligibleCount,
          stableCount,
          stableRate,
          totalGroups,
          meetsTarget,
          minEligible,
          targetStableRate,
        };

        if (!best) {
          best = candidate;
          return;
        }

        if (candidate.meetsTarget && !best.meetsTarget) {
          best = candidate;
          return;
        }

        if (candidate.meetsTarget === best.meetsTarget) {
          if (candidate.eligibleCount > best.eligibleCount) {
            best = candidate;
            return;
          }
          if (candidate.eligibleCount === best.eligibleCount) {
            if (candidate.stableRate > best.stableRate + 1e-6) {
              best = candidate;
              return;
            }
            if (Math.abs(candidate.stableRate - best.stableRate) < 1e-6) {
              const candidateSum = candidate.minEvents + candidate.minNonZeroYears + candidate.minSpanYears;
              const bestSum = best.minEvents + best.minNonZeroYears + best.minSpanYears;
              if (candidateSum < bestSum) best = candidate;
            }
          }
        }
      });
    });
  });

  return best;
};
